export default function ExpertiseSection() {
  const expertiseAreas = [
    {
      icon: 'ri-image-line',
      title: 'サムネイル最適化',
      description: 'データに基づいたビジュアルフックで、クリック率と視聴者エンゲージメントを最大化',
      color: 'from-pink-500 to-rose-500',
      image: 'https://static.readdy.ai/image/ec6c134e43b902bc81c674ec061ba5f4/471afad12d1f216d8f96c48aa201f6c9.png'
    },
    {
      icon: 'ri-layout-line',
      title: '配信レイアウトデザイン',
      description: 'プロフェッショナルな配信オーバーレイとインタラクティブなストリーミングインターフェース',
      color: 'from-purple-500 to-indigo-500',
      image: 'https://static.readdy.ai/image/ec6c134e43b902bc81c674ec061ba5f4/212d3cffbd03242711cb0a6fa0d425af.jpeg'
    },
    {
      icon: 'ri-user-star-line',
      title: 'キャラクターIP開発',
      description: 'コンセプトから納品まで、完全なブランドアイデンティティとキャラクターアセット制作',
      color: 'from-cyan-500 to-blue-500',
      image: 'https://static.readdy.ai/image/ec6c134e43b902bc81c674ec061ba5f4/e811cff9ea7ed25448157491ec0cabe4.png'
    },
    {
      icon: 'ri-t-shirt-line',
      title: '印刷物 & グッズ',
      description: 'グッズやプロモーション素材のための印刷対応デザイン',
      color: 'from-teal-500 to-emerald-500',
      image: 'https://static.readdy.ai/image/ec6c134e43b902bc81c674ec061ba5f4/b0b07c01ad0c782aa46f227ea89ff57b.jpeg'
    },
    {
      icon: 'ri-tools-fill',
      title: 'プロフェッショナルツールキット',
      description: '業界標準のデザイン・イラストソフトウェアの熟練した専門知識',
      color: 'from-indigo-500 to-purple-500',
      isToolkit: true,
      tools: [
        { name: 'Adobe Illustrator', level: '上級', icon: 'ri-pen-nib-line', color: 'text-orange-600' },
        { name: 'Adobe Photoshop', level: '上級', icon: 'ri-image-edit-line', color: 'text-blue-600' },
        { name: 'Clip Studio Paint', level: 'エキスパート', icon: 'ri-brush-line', color: 'text-green-600' },
        { name: 'Affinity Designer', level: '中級', icon: 'ri-layout-4-line', color: 'text-pink-600' },
        { name: 'Adobe After Effects', level: '初級', icon: 'ri-movie-line', color: 'text-purple-600' },
        { name: 'Adobe InDesign', level: '中級', icon: 'ri-file-text-line', color: 'text-red-600' },
      ]
    },
    {
      icon: 'ri-trademark-line',
      title: 'ブランディング & ブランドデザイン',
      description: 'ロゴデザイン、ブランド開発、ブランドアイデンティティマニュアルの制作',
      color: 'from-amber-500 to-orange-500',
      image: 'https://storage.readdy-site.link/project_files/ad497236-385f-4b6e-bb69-bc2f03e1f0bd/39fa3795-f1e0-4906-a749-03300134de4e_sidilogo.jpg?v=8fbf00c10ad3e0aad3b0ccb3112a728c'
    }
  ];

  return (
    <section className="py-32 bg-gradient-to-b from-white to-purple-50 relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-8">
        <div className="text-center mb-16">
          <h2 className="text-5xl font-bold text-gray-900 mb-4">コアスキル</h2>
          <p className="text-lg text-gray-600"> 優れた<span className="san-brainy">VTuber</span>コンテンツを作成するための専門スキルとプロフェッショナルツール</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {expertiseAreas.map((area, index) => (
            <div
              key={index}
              className="bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-2 cursor-pointer border border-gray-100"
            >
              {area.isToolkit ? (
                // Toolkit Card
                <div className="p-8">
                  <div className={`w-16 h-16 flex items-center justify-center rounded-xl bg-gradient-to-br ${area.color} mb-6`}>
                    <i className={`${area.icon} text-3xl text-white`}></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{area.title}</h3>
                  <p className="text-sm text-gray-600 mb-6">{area.description}</p>
                  
                  <div className="grid grid-cols-2 gap-3">
                    {area.tools?.map((tool, toolIndex) => (
                      <div
                        key={toolIndex}
                        className="flex items-center gap-2 p-2 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors"
                      >
                        <div className="w-8 h-8 flex items-center justify-center">
                          <i className={`${tool.icon} text-xl ${tool.color}`}></i>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-xs font-semibold text-gray-900 truncate">{tool.name}</div>
                          <div className="flex items-center gap-1">
                            <span className={`w-1.5 h-1.5 rounded-full ${
                              tool.level === 'エキスパート' ? 'bg-green-500' : 
                              tool.level === '上級' ? 'bg-blue-500' : 'bg-yellow-500'
                            }`}></span>
                            <span className="text-[10px] uppercase tracking-wide text-gray-500">{tool.level}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                // Regular Expertise Card
                <>
                  <div className="relative w-full h-48 bg-gray-100 overflow-hidden">
                    <img 
                      src={area.image} 
                      alt={area.title}
                      className="w-full h-full object-cover object-top"
                      loading="lazy"
                      decoding="async"
                    />
                    <div className={`absolute top-4 right-4 w-12 h-12 flex items-center justify-center rounded-xl bg-gradient-to-br ${area.color} shadow-lg`}>
                      <i className={`${area.icon} text-2xl text-white`}></i>
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{area.title}</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">{area.description}</p>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}